import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { SlidersHorizontal, X } from "lucide-react";
import { API } from "../lib/api";
import { useUserLocation, locParams, locLabel } from "../lib/location";
import { BRANDS, FUELS, TRANSMISSIONS, BODY_TYPES } from "../data/india";
import BackButton from "../components/BackButton";
import CarCard from "../components/CarCard";

const KEYS = ["q", "seller_type", "state", "city", "brand", "fuel", "transmission", "body_type", "min_price", "max_price", "min_year", "max_km", "max_owners", "color", "sort"];
const COLORS = ["White", "Black", "Silver", "Grey", "Red", "Blue", "Brown", "Green", "Orange", "Yellow"];

const Sel = ({ label, value, onChange, options, id, all = "Any" }) => (
  <div>
    <label className="lux-label">{label}</label>
    <select className="lux-select" value={value} onChange={(e) => onChange(e.target.value)} data-testid={id}>
      <option value="">{all}</option>
      {options.map((o) => (Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o}>{o}</option>))}
    </select>
  </div>
);

export default function Browse() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(() => window.innerWidth >= 1024);
  const [enabled, setEnabled] = useState({});
  useEffect(() => { axios.get(`${API}/settings/filters`).then((r) => setEnabled(r.data)).catch(() => {}); }, []);
  const f = Object.fromEntries(KEYS.map((k) => [k, params.get(k) || ""]));
  const page = Number(params.get("page") || 1);
  const { location, setLocation } = useUserLocation();
  const lp = locParams(location);
  const locKey = JSON.stringify(lp);

  useEffect(() => {
    // Legacy links (?state=&city=) move into the global top-bar location, then leave the URL.
    if (!params.get("state") && !params.get("city") && !params.get("all")) return;
    if (params.get("state")) setLocation({ state: params.get("state"), city: params.get("city") || "" });
    else if (params.get("all")) setLocation(null);
    const n = new URLSearchParams(params);
    ["state", "city", "all"].forEach((k) => n.delete(k));
    setParams(n, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.toString()]);
  const needsLoc = !!(params.get("state") || params.get("city") || params.get("all"));

  useEffect(() => {
    if (needsLoc) return;
    let stale = false;
    setLoading(true);
    const p = Object.fromEntries(Object.entries(f).filter(([k, v]) => v && k !== "state" && k !== "city"));
    axios.get(`${API}/listings`, { params: { ...p, ...lp, page, limit: 16 } })
      .then((r) => { if (!stale) setData(r.data); })
      .catch(() => { if (!stale) setData({ items: [], total: 0, pages: 1 }); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.toString(), needsLoc, locKey]);

  const update = (patch) => {
    const n = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k)));
    n.delete("page");
    setParams(n);
  };
  const years = Array.from({ length: 16 }, (_, i) => String(new Date().getFullYear() - i));
  const title = f.seller_type === "private" ? "Private Owner Cars" : f.seller_type === "dealer" ? "Dealer Store Cars" : "All Pre-Owned Cars";

  const on = (k) => enabled[k] !== false;
  const activeCount = ["seller_type", "brand", "fuel", "transmission", "body_type", "min_year", "min_price", "max_price", "max_km", "max_owners", "color"].filter((k) => f[k]).length;
  const filters = (
    <div className="lux-card p-4 space-y-3" data-testid="filter-panel">
      <div className="flex items-center justify-between gap-2"><BackButton label="Close" onClick={() => setShowFilters(false)} testid="filter-back" className="!py-1 !px-2.5" /><p className="eyebrow flex-1">Refine {activeCount > 0 && <span className="text-gray-400 normal-case tracking-normal">· {activeCount} active</span>}</p><button onClick={() => setParams({})} className="text-xs text-gray-400 hover:text-[#E5C158]" data-testid="filter-reset">Reset all</button></div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 items-end">
        {on("seller_type") && (
          <div className="col-span-2 md:col-span-1">
            <label className="lux-label">Seller type</label>
            <div className="grid grid-cols-3 gap-1 p-1 rounded-full bg-black/50 border border-white/5">
              {[["", "All"], ["private", "Owner"], ["dealer", "Dealer"]].map(([v, l]) => (
                <button key={v} onClick={() => update({ seller_type: v })} data-testid={`filter-seller-${v || "all"}`}
                  className={`text-[11px] py-1 rounded-full font-semibold transition-colors ${f.seller_type === v ? "text-black" : "text-gray-400 hover:text-white"}`} style={f.seller_type === v ? { background: "var(--gold-grad)" } : {}}>{l}</button>
              ))}
            </div>
          </div>
        )}
        {on("brand") && <Sel label="Brand" value={f.brand} onChange={(v) => update({ brand: v })} options={BRANDS} id="filter-brand-select" />}
        {on("price") && <>
          <div><label className="lux-label">Min ₹</label><input key={`min-${f.min_price}`} className="lux-input" type="number" defaultValue={f.min_price} onBlur={(e) => update({ min_price: e.target.value })} data-testid="filter-min-price" /></div>
          <div><label className="lux-label">Max ₹</label><input key={`max-${f.max_price}`} className="lux-input" type="number" defaultValue={f.max_price} onBlur={(e) => update({ max_price: e.target.value })} data-testid="filter-max-price" /></div>
        </>}
        {on("fuel") && <Sel label="Fuel" value={f.fuel} onChange={(v) => update({ fuel: v })} options={FUELS} id="filter-fuel-select" />}
        {on("transmission") && <Sel label="Gearbox" value={f.transmission} onChange={(v) => update({ transmission: v })} options={TRANSMISSIONS} id="filter-transmission-select" />}
        {on("body_type") && <Sel label="Body type" value={f.body_type} onChange={(v) => update({ body_type: v })} options={BODY_TYPES} id="filter-body-select" />}
        {on("min_year") && <Sel label="Year from" value={f.min_year} onChange={(v) => update({ min_year: v })} options={years} id="filter-year-select" />}
        {on("max_km") && <Sel label="Max KM" value={f.max_km} onChange={(v) => update({ max_km: v })} options={["10000", "30000", "50000", "75000", "100000"]} id="filter-km-select" />}
        {on("max_owners") && <Sel label="Max owners" value={f.max_owners} onChange={(v) => update({ max_owners: v })} options={["1", "2", "3"]} id="filter-owners-select" />}
        {on("color") && <Sel label="Colour" value={f.color} onChange={(v) => update({ color: v })} options={COLORS} id="filter-color-select" />}
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28" data-testid="browse-page">
      <BackButton className="mb-3" />
      <p className="eyebrow !text-[10px] mb-1" data-testid="browse-location">{location?.state ? locLabel(location) : "All India"}</p>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <h1 className="font-display text-3xl sm:text-4xl">{title}</h1>
        <div className="flex gap-2 items-center w-full sm:w-auto">
          <input key={`q-${f.q}`} className="lux-input sm:w-56" placeholder="Search model, city..." defaultValue={f.q} onKeyDown={(e) => e.key === "Enter" && update({ q: e.target.value })} data-testid="browse-search-input" />
          <select className="lux-select !w-40" value={f.sort} onChange={(e) => update({ sort: e.target.value })} data-testid="browse-sort-select">
            <option value="">Newest first</option><option value="price_asc">Price: Low to High</option><option value="price_desc">Price: High to Low</option><option value="km_asc">Lowest KM</option><option value="year_desc">Newest model</option>
          </select>
          <button className={`btn-outline !px-3 relative ${showFilters ? "!bg-[#D4AF37]/15" : ""}`} onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters">{showFilters ? <X className="w-4 h-4" /> : <SlidersHorizontal className="w-4 h-4" />}<span className="hidden sm:inline">Filters</span>
            {activeCount > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] rounded-full text-[10px] font-bold text-black flex items-center justify-center" style={{ background: "var(--gold-grad)" }} data-testid="filter-active-count">{activeCount}</span>}</button>
        </div>
      </div>
      <div className="grid transition-[grid-template-rows,opacity] duration-300 ease-in-out mb-4" style={{ gridTemplateRows: showFilters ? "1fr" : "0fr", opacity: showFilters ? 1 : 0 }} data-testid="filter-drawer" data-open={showFilters}>
        <aside className={`overflow-hidden min-w-0 transition-[visibility] duration-300 ${showFilters ? "visible" : "invisible"}`} aria-hidden={!showFilters}>{filters}</aside>
      </div>
      <div>
        <div className="min-w-0">
          <p className="text-xs text-gray-400 mb-3" data-testid="results-count">{loading ? "Loading..." : `${data.total} cars found`}</p>
          {!loading && data.items.length === 0 && <div className="lux-card p-16 text-center text-gray-400" data-testid="no-results">No cars match these filters. Try widening your search.</div>}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">{data.items.map((c, i) => <CarCard key={c.id} car={c} index={i} />)}</div>
          {data.pages > 1 && (
            <div className="flex justify-center gap-2 mt-8" data-testid="pagination">
              {Array.from({ length: data.pages }, (_, i) => i + 1).map((p) => (
                <button key={p} onClick={() => { const n = new URLSearchParams(params); n.set("page", p); setParams(n); window.scrollTo(0, 0); }}
                  className={`w-8 h-8 rounded-full text-xs ${p === page ? "text-black font-bold" : "border border-white/10 text-gray-300"}`} style={p === page ? { background: "var(--gold-grad)" } : {}} data-testid={`page-${p}`}>{p}</button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
